import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Terminal, ShieldCheck, RefreshCw, Cpu, Info, Download, FileCode } from "lucide-react";
import Header from "@/components/Header";
import { useLang } from "@/i18n";
import { downloadBlob } from "@/lib/export";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function encodePwsh(text) {
  let bin = "";
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    bin += String.fromCharCode(c & 0xff, (c >> 8) & 0xff);
  }
  return btoa(bin);
}

function CodeBlock({ code, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Kopiert / Copied");
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <div className="relative group">
      <button
        data-testid={testid ? `${testid}-copy` : "code-copy"}
        onClick={copy}
        className="absolute top-3 right-3 flex items-center gap-1.5 text-xs font-mono px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors z-10"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-[#c00000]" /> : <Copy className="w-3.5 h-3.5" />}
        {copied ? "OK" : "Copy"}
      </button>
      <pre
        data-testid={testid}
        className="bg-white border border-gray-200 rounded-xl p-4 pr-20 overflow-x-auto text-xs font-mono text-gray-700 leading-relaxed max-h-[520px]"
      >
        {code}
      </pre>
    </div>
  );
}

export default function Installer() {
  const { t, lang } = useLang();
  const [apiKey, setApiKey] = useState("<AGENT_API_KEY>");
  const [deviceCode, setDeviceCode] = useState("");
  const [tab, setTab] = useState("install");

  useEffect(() => {
    api.get("/agent/config").then((r) => setApiKey(r.data.api_key)).catch(() => {});
  }, []);

  const heartbeatUrl = `${BACKEND_URL}/api/agent/heartbeat`;
  const payloadUrl = `${BACKEND_URL}/api/agent/payload`;

  const { installScript, coreScript } = useMemo(() => {
    const dc = deviceCode.trim();
    const core = `$Backend = "${heartbeatUrl}"
$Payload  = "${payloadUrl}"
$ApiKey   = "${apiKey}"
$Dir      = "$env:ProgramData\\FleetAgent"
New-Item -ItemType Directory -Force -Path $Dir | Out-Null

# --- Write agent bootstrap (fixed & immutable; self-updates the payload) ---
$agent = @'
$Backend = "__BACKEND__"
$Payload = "__PAYLOAD__"
$ApiKey  = "__APIKEY__"
$DeviceCode = "__DEVICECODE__"
$Dir = "$env:ProgramData\\FleetAgent"
$GoodFile = "$Dir\\payload_good.ps1"
try {
  $cs   = Get-CimInstance Win32_ComputerSystem
  $csp  = Get-CimInstance Win32_ComputerSystemProduct
  $bios = Get-CimInstance Win32_BIOS
  $os   = Get-CimInstance Win32_OperatingSystem
  $tvId = ""
  foreach ($p in @("HKLM:\\SOFTWARE\\WOW6432Node\\TeamViewer","HKLM:\\SOFTWARE\\TeamViewer")) {
    try { $v = (Get-ItemProperty -Path $p -ErrorAction Stop).ClientID; if ($v) { $tvId = "$v"; break } } catch {}
  }
  # --- Zentrales Payload laden, SHA256 prüfen, isoliert ausführen ---
  $plVer = 0; $plStatus = "ok"; $plErr = ""; $plChan = "stable"; $extra = @{}
  try {
    $plUrl = "$Payload" + "?code=" + [uri]::EscapeDataString("$DeviceCode") + "&uid=" + [uri]::EscapeDataString("$($csp.UUID)")
    $pl = Invoke-RestMethod -Uri $plUrl -Headers @{ "X-Agent-Key" = $ApiKey } -TimeoutSec 20
    $plVer = [int]$pl.version
    if ($pl.channel) { $plChan = "$($pl.channel)" }
    $sha = [System.BitConverter]::ToString([System.Security.Cryptography.SHA256]::Create().ComputeHash([System.Text.Encoding]::UTF8.GetBytes([string]$pl.script))).Replace("-","").ToLower()
    if ($sha -ne ([string]$pl.hash).ToLower()) { throw "hash mismatch" }
    $sb = [ScriptBlock]::Create([string]$pl.script)
    $extra = & $sb
    if ($extra -isnot [hashtable]) { $extra = @{} }
    Set-Content -Path $GoodFile -Value ([string]$pl.script) -Encoding UTF8
  } catch {
    $plStatus = "error"; $plErr = "$($_.Exception.Message)"
    try {
      if (Test-Path $GoodFile) {
        $sbg = [ScriptBlock]::Create((Get-Content -Raw -Path $GoodFile))
        $extra = & $sbg
        if ($extra -isnot [hashtable]) { $extra = @{} }
      }
    } catch { $extra = @{} }
  }
  $body = @{
    device_uid    = "$($csp.UUID)"
    hostname      = "$env:COMPUTERNAME"
    tablet_serial = "$($bios.SerialNumber)"
    manufacturer  = "$($cs.Manufacturer)"
    model         = "$($cs.Model)"
    teamviewer_id = "$tvId"
    os_version    = "$($os.Caption) $($os.Version)"
    agent_version = "2.0.0"
    payload_version = $plVer
    payload_status  = $plStatus
    payload_error   = $plErr
    payload_channel = $plChan
  }
  if ($DeviceCode) { $body.device_code = $DeviceCode }
  foreach ($k in $extra.Keys) { $body[$k] = $extra[$k] }
  $json = $body | ConvertTo-Json -Compress
  Invoke-RestMethod -Uri $Backend -Method Post -Body $json -ContentType "application/json" -Headers @{ "X-Agent-Key" = $ApiKey } -TimeoutSec 20 | Out-Null
} catch { }
'@
$agent = $agent.Replace("__BACKEND__", $Backend).Replace("__PAYLOAD__", $Payload).Replace("__APIKEY__", $ApiKey).Replace("__DEVICECODE__", $DeviceCode)
Set-Content -Path "$Dir\\agent.ps1" -Value $agent -Encoding UTF8

# --- Scheduled task: at startup + every 60s, as SYSTEM, highest rights ---
$action = New-ScheduledTaskAction -Execute "powershell.exe" \`
  -Argument "-NoProfile -WindowStyle Hidden -ExecutionPolicy Bypass -File \`"$Dir\\agent.ps1\`""
$tRepeat  = New-ScheduledTaskTrigger -Once -At (Get-Date) \`
  -RepetitionInterval (New-TimeSpan -Seconds 60) -RepetitionDuration (New-TimeSpan -Days 3650)
$tStartup = New-ScheduledTaskTrigger -AtStartup
$principal = New-ScheduledTaskPrincipal -UserId "SYSTEM" -LogonType ServiceAccount -RunLevel Highest
$settings  = New-ScheduledTaskSettingsSet -AllowStartIfOnBatteries -DontStopIfGoingOnBatteries \`
  -StartWhenAvailable -MultipleInstances IgnoreNew -RestartCount 3 -RestartInterval (New-TimeSpan -Minutes 1)
Register-ScheduledTask -TaskName "FleetAgent" -Action $action -Trigger @($tRepeat, $tStartup) \`
  -Principal $principal -Settings $settings -Force | Out-Null
Start-ScheduledTask -TaskName "FleetAgent"
Write-Host "Fleet Area installiert & gestartet. / installed & running." -ForegroundColor Green`;

    const install = `param([string]$DeviceCode = ${dc ? `"${dc}"` : '""'})

# ============================================================
#  Fleet Area - Windows Installer  (auto-elevation, auto-start)
# ============================================================
$ErrorActionPreference = "Stop"

# --- Auto-elevate to Administrator if needed ---
$id = [Security.Principal.WindowsIdentity]::GetCurrent()
$pr = New-Object Security.Principal.WindowsPrincipal($id)
if (-not $pr.IsInRole([Security.Principal.WindowsBuiltInRole]::Administrator)) {
  $argLine = "-NoProfile -ExecutionPolicy Bypass -File \`"$PSCommandPath\`""
  if ($DeviceCode) { $argLine += " -DeviceCode \`"$DeviceCode\`"" }
  Start-Process powershell.exe -Verb RunAs -ArgumentList $argLine
  exit
}

${core}

# --- Erfolg + Countdown + automatischer Neustart ---
Write-Host ""
Write-Host "==================================================" -ForegroundColor Green
Write-Host "   Installation erfolgreich / successful" -ForegroundColor Green
Write-Host "==================================================" -ForegroundColor Green
for ($i = 5; $i -ge 1; $i--) {
  Write-Host "   Neustart in $i Sekunden... / Restarting in $i s..." -ForegroundColor Yellow
  Start-Sleep -Seconds 1
}
Write-Host "   Neustart... / Restarting..." -ForegroundColor Yellow
Restart-Computer -Force`;

    const coreFull = `$ErrorActionPreference = "Stop"
$DeviceCode = ${dc ? `"${dc}"` : '""'}
${core}`;

    return { installScript: install, coreScript: coreFull };
  }, [apiKey, deviceCode, heartbeatUrl, payloadUrl]);

  const cmdContent = useMemo(() => {
    const b64 = encodePwsh(coreScript);
    return `@echo off
title Fleet Area Installer
net session >nul 2>&1
if %errorlevel% NEQ 0 (
  echo Administrator-Rechte werden angefordert...
  powershell -NoProfile -Command "Start-Process -FilePath '%~f0' -Verb RunAs"
  exit /b
)
echo Fleet Area wird installiert...
powershell -NoProfile -ExecutionPolicy Bypass -EncodedCommand ${b64}
echo.
echo ==================================================
echo    Installation erfolgreich / successful
echo ==================================================
for /l %%i in (5,-1,1) do (
  echo    Neustart in %%i Sekunden... / Restarting in %%i s...
  ping -n 2 127.0.0.1 >nul
)
echo    Neustart... / Restarting...
shutdown /r /t 0 /c "Fleet Area installiert - Neustart"
`;
  }, [coreScript]);

  const suffix = deviceCode.trim() ? `-${deviceCode.trim()}` : "";

  const tabs = [
    { key: "install", label: lang === "de" ? "Installations-Skript" : "Install script", Icon: Terminal },
    { key: "how", label: lang === "de" ? "So funktioniert's" : "How it works", Icon: Info },
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main className="w-full px-4 sm:px-6 py-8 space-y-6">
        <div className="fade-up">
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{t("installer_title")}</h1>
          <p className="text-sm text-gray-600 mt-2 max-w-2xl">{t("installer_desc")}</p>
        </div>

        <div className="card-surface rounded-xl p-5 border border-[#c00000]/25 space-y-3" data-testid="oneliner-card">
          <div className="flex items-center gap-2 text-[#c00000] font-semibold">
            <Terminal className="w-4 h-4" /> {lang === "de" ? "Schnell-Installation (z. B. per TeamViewer)" : "Quick install (e.g. via TeamViewer)"}
          </div>
          <p className="text-sm text-gray-600">
            {lang === "de"
              ? "Auf dem Tablet eine PowerShell als Administrator öffnen und diese eine Zeile einfügen – kein Download nötig. Optional oben den Geräte-Code eintragen."
              : "On the tablet, open PowerShell as administrator and paste this one line – no download needed. Optionally set the device code above."}
          </p>
          <CodeBlock
            testid="oneliner-code"
            code={`powershell -NoProfile -ExecutionPolicy Bypass -Command "irm '${BACKEND_URL}/api/agent/install.ps1?code=${encodeURIComponent(deviceCode.trim())}&base=${encodeURIComponent(BACKEND_URL)}' | iex"`}
          />
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          {[
            { Icon: ShieldCheck, title: lang === "de" ? "Auto-Elevation" : "Auto elevation", desc: lang === "de" ? "Fragt automatisch Admin-Rechte an." : "Requests admin rights automatically." },
            { Icon: RefreshCw, title: lang === "de" ? "Autostart" : "Auto start", desc: lang === "de" ? "Startet nach jedem Neustart & alle 60s." : "Runs after every reboot & every 60s." },
            { Icon: Cpu, title: "SMBIOS UUID", desc: lang === "de" ? "Hardwarebasierte, eindeutige Geräte-UID." : "Hardware-based unique device UID." },
          ].map((c, i) => (
            <div key={i} className="card-surface rounded-xl p-4 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
              <c.Icon className="w-5 h-5 text-[#c00000] mb-2" />
              <div className="font-semibold text-sm">{c.title}</div>
              <div className="text-xs text-gray-600 mt-1">{c.desc}</div>
            </div>
          ))}
        </div>

        <div className="card-surface rounded-xl p-5">
          <label className="text-xs font-mono uppercase tracking-wider text-gray-600 mb-2 block">
            {lang === "de" ? "Optionaler Geräte-Code (z. B. FRAT03-01) – leer = Hostname" : "Optional device code (e.g. FRAT03-01) – empty = hostname"}
          </label>
          <input
            data-testid="installer-devicecode"
            value={deviceCode}
            onChange={(e) => setDeviceCode(e.target.value)}
            placeholder="FRAT03-01"
            className="w-full sm:w-72 bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono text-gray-900 focus:border-[#c00000] focus:ring-1 focus:ring-[#c00000] outline-none"
          />
        </div>

        {/* Download buttons */}
        <div className="card-surface rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Download className="w-4 h-4 text-[#c00000]" />
            <span className="text-sm font-semibold">{lang === "de" ? "Fertige Datei herunterladen" : "Download ready file"}</span>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              data-testid="download-installer-cmd"
              onClick={() => {
                downloadBlob(cmdContent, `install-agent${suffix}.cmd`, "application/octet-stream");
                toast.success(lang === "de" ? "Installer heruntergeladen" : "Installer downloaded");
              }}
              className="flex items-center gap-2 bg-[#c00000] hover:bg-[#c00000] text-white font-semibold rounded-lg px-4 py-2.5 text-sm transition-colors"
            >
              <Download className="w-4 h-4" /> {t("download_installer")}
            </button>
            <button
              data-testid="download-installer-ps1"
              onClick={() => {
                downloadBlob(installScript, `install-agent${suffix}.ps1`, "application/octet-stream");
                toast.success(lang === "de" ? "Skript heruntergeladen" : "Script downloaded");
              }}
              className="flex items-center gap-2 bg-gray-50 border border-gray-200 text-gray-800 rounded-lg px-4 py-2.5 text-sm hover:bg-gray-100 transition-colors"
            >
              <FileCode className="w-4 h-4" /> {t("download_ps1")}
            </button>
          </div>
          <p className="text-xs text-gray-600 mt-3 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            {t("download_exe_note")}
          </p>
        </div>

        <div className="flex gap-2 border-b border-gray-200">
          {tabs.map((tb) => (
            <button
              key={tb.key}
              data-testid={`installer-tab-${tb.key}`}
              onClick={() => setTab(tb.key)}
              className={`flex items-center gap-2 text-sm px-3 py-2.5 -mb-px border-b-2 transition-colors ${
                tab === tb.key ? "border-[#c00000] text-gray-900" : "border-transparent text-gray-600 hover:text-gray-700"
              }`}
            >
              <tb.Icon className="w-4 h-4" /> {tb.label}
            </button>
          ))}
        </div>

        {tab === "install" ? (
          <div className="space-y-4">
            <ol className="text-sm text-gray-600 space-y-1.5 list-decimal list-inside">
              <li>{lang === "de" ? "Empfohlen: oben " : "Recommended: click "}<span className="font-mono text-[#c00000]">Installer (.cmd)</span>{lang === "de" ? " herunterladen und doppelklicken." : " above and double-click it."}</li>
              <li>{lang === "de" ? "Alternativ: Skript unten als " : "Alternatively: save the script below as "}<span className="font-mono text-[#c00000]">install-agent.ps1</span>{lang === "de" ? " speichern, Rechtsklick → „Mit PowerShell ausführen“." : ", right-click → \"Run with PowerShell\"."}</li>
              <li>{lang === "de" ? "Admin-Abfrage bestätigen – das Gerät erscheint in wenigen Sekunden oben in der Übersicht." : "Confirm the admin prompt – the device appears in the overview within seconds."}</li>
            </ol>
            <CodeBlock code={installScript} testid="install-script" />
          </div>
        ) : (
          <div className="space-y-4 text-sm text-gray-600 leading-relaxed card-surface rounded-xl p-6">
            <p><span className="text-gray-800 font-medium">device_uid:</span> {lang === "de" ? "Aus der SMBIOS-System-UUID (Win32_ComputerSystemProduct.UUID) – hardwaregebunden und eindeutig." : "From the SMBIOS system UUID – hardware-bound and unique."}</p>
            <p><span className="text-gray-800 font-medium">device_code / location_id:</span> {lang === "de" ? "device_code z. B. FRAT03-01; die Standort-ID (location_id) wird automatisch daraus abgeleitet (FRAT03)." : "device_code e.g. FRAT03-01; location_id is derived automatically (FRAT03)."}</p>
            <p><span className="text-gray-800 font-medium">tablet_serial:</span> {lang === "de" ? "Hersteller-Seriennummer des Tablets (Win32_BIOS.SerialNumber)." : "Manufacturer serial of the tablet (Win32_BIOS.SerialNumber)."}</p>
            <p><span className="text-gray-800 font-medium">teamviewer_id:</span> {lang === "de" ? "Aus der Registry (TeamViewer ClientID), immer ohne Leerzeichen; 1-Klick-Direktverbindung im Dashboard." : "From registry (TeamViewer ClientID), always without spaces; 1-click direct connect in the dashboard."}</p>
            <p><span className="text-gray-800 font-medium">{lang === "de" ? "Autostart:" : "Auto start:"}</span> {lang === "de" ? "Geplante Aufgabe unter dem SYSTEM-Konto – bei Systemstart und alle 60 Sekunden, auch ohne angemeldeten Benutzer und nach jedem Neustart." : "Scheduled task under SYSTEM – at boot and every 60 seconds, even without a logged-in user."}</p>
            <p><span className="text-gray-800 font-medium">{lang === "de" ? "Erweiterbar:" : "Extensible:"}</span> {lang === "de" ? "Beliebige weitere Felder einfach zum $body im Agent-Skript hinzufügen (z. B. asset_tag, battery_health) – sie werden automatisch gespeichert, versioniert und im Detail-Bereich sowie im CSV-Export angezeigt." : "Add any extra keys to $body in the agent script (e.g. asset_tag, battery_health) – they are stored, versioned and shown in the detail view and CSV export automatically."}</p>
            <div className="pt-2 border-t border-gray-100">
              <div className="text-xs font-mono uppercase tracking-wider text-gray-600 mb-2">Endpoint</div>
              <code className="text-xs text-[#c00000] break-all">POST {heartbeatUrl}</code>
              <div className="text-xs text-gray-600 mt-1">Header: <span className="font-mono text-gray-600">X-Agent-Key</span></div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
